import { Injectable } from '@angular/core';
import { BaseCondition } from '../models/base-condition.model';
import { AndCondition, CompositeCondition, OrCondition } from '../models/composite-conditions.model';
import { Condition } from '../models/condition.model';
import { EAlertTypes } from '../../dialogs/alert/alert.component';
import { TranslateService } from '@ngx-translate/core';
import { DialogService } from '../../dialogs/dialog.service';
import { FormControl, FormGroup, ValidationErrors } from '@angular/forms';
import { Subject } from 'rxjs';

interface IRemovedConditionNode {
  condition: BaseCondition;
  previousIndex: number;
}

@Injectable()
export class ConditionDialogService {
  selectedConditions: BaseCondition[] = [];

  allSelectedFromSameParent: boolean = false;
  allChildrenOfSameParentSelected: boolean = false;
  parentOfAllSelected: CompositeCondition = null;
  conditionsForm: FormGroup;
  conditionNumberGenerator: number = 1;
  showRequiredErrorMessage: boolean = false;
  showDatesBetweenErrorMessage: boolean = false;
  showNumberMaxErrorMessage: boolean = false;
  showDecimalMaxErrorMessage: boolean = false;
  showEntityAttributeErrorMessage: boolean = false;
  invalidCRMAttributesConditionsNumbers: number[] = [];

  readonly MaxConditionTreeHeight = 3;

  constructor(private dialogService: DialogService,
              private translate: TranslateService) {
    this.recreateFormValidationGroup();
  }

  public doCheckValidity: Subject<any> = new Subject();

  initiateCheckValidityEvent() {
    this.doCheckValidity.next();
  }

  getConditionNumberFromGenerator() {
    const conditionNumberGenerator = this.conditionNumberGenerator++;
    return conditionNumberGenerator;
  }

  addControlToGroup(formControl: FormControl, controlName: string) {
    this.conditionsForm.addControl(controlName, formControl);
  }

  removeControlFromGroup(controlName: string) {
    const formControl = this.conditionsForm.get(controlName);
    if (formControl) {
      this.conditionsForm.removeControl(controlName);
    }
  }

  agregateFormValidationErrors() {
    this.showRequiredErrorMessage = false;
    this.showDatesBetweenErrorMessage = false;
    this.showNumberMaxErrorMessage = false;
    this.showDecimalMaxErrorMessage = false;

    Object.keys(this.conditionsForm.controls).forEach(key => {
      const formControl = this.conditionsForm.get(key);
      if (formControl && formControl.touched) {
        const controlErrors: ValidationErrors = formControl.errors;
        if (controlErrors) {
          if (!this.showRequiredErrorMessage) {
            this.showRequiredErrorMessage = controlErrors.required;
          }
          if (!this.showDatesBetweenErrorMessage) {
            this.showDatesBetweenErrorMessage = controlErrors.firstDateIsGreater && controlErrors.firstDateIsGreater.value;
          }
          if (!this.showNumberMaxErrorMessage) {
            this.showNumberMaxErrorMessage = controlErrors.numberIsGreater && controlErrors.numberIsGreater.value;
          }
          if (!this.showDecimalMaxErrorMessage) {
            this.showDecimalMaxErrorMessage = controlErrors.decimalIsGreater && controlErrors.decimalIsGreater.value;
          }
        }
      }
    });
  }

  markAllControlsAsTouched() {
    Object.keys(this.conditionsForm.controls).forEach(key => {
      const formControl = this.conditionsForm.get(key);
      if (formControl) {
        formControl.markAsTouched();
        setTimeout(() => {
          formControl.updateValueAndValidity();
        }, 0);
      }
    });
  }

  addInvalidCondition(conditionNumber: number) {
    const existingConditionNumber: number = this.invalidCRMAttributesConditionsNumbers.find(condition => condition === conditionNumber);
    if (!existingConditionNumber) {
      this.invalidCRMAttributesConditionsNumbers.push(conditionNumber);
    }
    if (this.invalidCRMAttributesConditionsNumbers.length > 0) {
      this.showEntityAttributeErrorMessage = true;
    }
  }

  removeInvalidCondition(conditionNumber: number) {
    const index = this.invalidCRMAttributesConditionsNumbers.indexOf(conditionNumber, 0);
    if (index > -1) {
      this.invalidCRMAttributesConditionsNumbers.splice(index, 1);
    }

    if (this.invalidCRMAttributesConditionsNumbers.length === 0) {
      this.showEntityAttributeErrorMessage = false;
    }
  }

  recreateFormValidationGroup() {
    this.conditionsForm = new FormGroup({});
  }

  addConditionToSelected(conditionSelected: BaseCondition) {
    if (!conditionSelected.parent) {
      throw new Error('tried to select a condition without a parent. this should not have happened');
    }

    this.selectedConditions.push(conditionSelected);
    this.updateServiceStates();
  }

  clearSelection() {
    this.selectedConditions = [];
    this.updateServiceStates();
  }

  removeConditionFromSelected(conditionSelected: BaseCondition) {
    const childIndex: number = this.selectedConditions.indexOf(conditionSelected);
    if (childIndex === -1) {
      throw new Error(`could not locate the selected conditions on the internal list: ${ JSON.stringify(conditionSelected) }`);
    }

    this.selectedConditions.splice(childIndex, 1);
    this.updateServiceStates();
  }

  reduceConditionTreeAfterDelete(rootCondition: BaseCondition) {
    if (rootCondition instanceof Condition) {
      return;
    }

    //if we are here this is a composite condition
    const compositeCondition: CompositeCondition = rootCondition as CompositeCondition;
    //call the recursion now to do the operation post-order
    compositeCondition.childConditions.forEach((childCondition) => {
      this.reduceConditionTreeAfterDelete(childCondition);
    });

    //after we reduced all our children check ourselves
    if (compositeCondition.childConditions.length === 0 && compositeCondition.parent !== null) {
      //if we are left without children at all remove ourselfs from our parents's list
      //only if we are not at the root (composite) conditions
      compositeCondition.parent.removeChildCondition(rootCondition);
    } else if (compositeCondition.childConditions.length === 1 && compositeCondition.parent !== null) {
      //remove me (the composite condition) and make the single condition on my children list a child on my parent instead of me.
      //only if we are not at the root (composite) conditions
      const conditionParent = compositeCondition.parent; //save the parent since it will be removed from the condition we are removing
      conditionParent.removeChildCondition(compositeCondition);
      conditionParent.addChildCondition(compositeCondition.childConditions[0]);
    }
  }

  groupSelectedConditions(isOrGroup: boolean) {
    //check that this addition will not overflow the allowed height
    let heightAfterAdditionPrediction = 0;

    //get the max height of all the children we will add to the new composite condition
    let maxSlectedConditionHeight = 0;
    this.selectedConditions.forEach((selectedCondition: BaseCondition) => {
      if (selectedCondition.getHeight() > maxSlectedConditionHeight) {
        maxSlectedConditionHeight = selectedCondition.getHeight();
      }
    });

    heightAfterAdditionPrediction += maxSlectedConditionHeight;
    heightAfterAdditionPrediction += this.parentOfAllSelected.getDepth();
    heightAfterAdditionPrediction += 1;
    if (heightAfterAdditionPrediction > this.MaxConditionTreeHeight) {
      this.dialogService.alert({
        header: this.translate.instant('COMMON.DIALOGS.CONDITIONS.ERROR_GROUPING.TITLE'),
        message: this.translate.instant('COMMON.DIALOGS.CONDITIONS.ERROR_GROUPING.TEXT'),
        type: EAlertTypes.Warning,
        okCaption: this.translate.instant('COMMON.DIALOGS.CONDITIONS.ERROR_GROUPING.OK_CAPTION')
      });

      return; //we don't do any action here. just inform the user.
    }

    let newCompositeCondition: CompositeCondition;
    if (isOrGroup) {
      newCompositeCondition = new OrCondition();
    } else {
      newCompositeCondition = new AndCondition();
    }

    //find the first index of the selected conditions. this is were we shell place the newly created composite condition
    let firstChildIndexOfSelectedConditions = this.parentOfAllSelected.childConditions.indexOf(this.selectedConditions[0]); //take the first selected condition as a value to start with
    this.selectedConditions.forEach((selectedCondition) => {
      const currentSelectedConditionIndexInParent = this.parentOfAllSelected.childConditions.indexOf(selectedCondition);
      if (firstChildIndexOfSelectedConditions) {
        if (firstChildIndexOfSelectedConditions > currentSelectedConditionIndexInParent) {
          firstChildIndexOfSelectedConditions = currentSelectedConditionIndexInParent;
        }
      }
    });

    //take out the child conditions that were selected while saving the index of where they used to be so they can be insert back in the same
    //relative order

    let removedConditions: IRemovedConditionNode[] = [];
    this.selectedConditions.forEach((selectedCondition) => {
      removedConditions.push({
        condition: selectedCondition,
        previousIndex: this.parentOfAllSelected.childConditions.indexOf(selectedCondition)
      });
    });

    removedConditions = removedConditions.sort((a: IRemovedConditionNode, b: IRemovedConditionNode) => {
      return a.previousIndex - b.previousIndex;
    });

    removedConditions.forEach((removedConditionNode: IRemovedConditionNode) => {
      this.parentOfAllSelected.removeChildCondition(removedConditionNode.condition);
      newCompositeCondition.addChildCondition(removedConditionNode.condition);
    });

    //place the newly created composite condition in the first index of the selected so it will not move so much on the condition tree.
    this.parentOfAllSelected.insertChildConditionAtIndex(firstChildIndexOfSelectedConditions, newCompositeCondition);
    this.clearSelection();
  }

  ungroupSelection() {
    const newParentForUngourpedConditions = this.parentOfAllSelected.parent;
    let indexOfContainingParent = newParentForUngourpedConditions.childConditions.indexOf(this.parentOfAllSelected);

    newParentForUngourpedConditions.removeChildCondition(this.parentOfAllSelected);

    let removedConditions: IRemovedConditionNode[] = [];
    this.selectedConditions.forEach((selectedCondition) => {
      removedConditions.push({
        condition: selectedCondition,
        previousIndex: this.parentOfAllSelected.childConditions.indexOf(selectedCondition)
      });
    });

    removedConditions = removedConditions.sort((a: IRemovedConditionNode, b: IRemovedConditionNode) => {
      return a.previousIndex - b.previousIndex;
    });

    removedConditions.forEach((selectedConditionNode) => {
      this.parentOfAllSelected.removeChildCondition(selectedConditionNode.condition);
      newParentForUngourpedConditions.insertChildConditionAtIndex(indexOfContainingParent, selectedConditionNode.condition);
      indexOfContainingParent++;
    });

    this.clearSelection();
  }

  private updateServiceStates() {
    if (this.selectedConditions.length > 0) {
      const parent = this.selectedConditions[0].parent;
      let allSelectedHasTheSameParent = true;
      for (let i = 0; i < this.selectedConditions.length; i++) {
        const currentCondition = this.selectedConditions[i];
        if (currentCondition.parent !== parent) {
          allSelectedHasTheSameParent = false;
          break;
        }
      }

      this.allSelectedFromSameParent = allSelectedHasTheSameParent;

      if (this.allSelectedFromSameParent) {
        this.parentOfAllSelected = parent;
      } else {
        this.parentOfAllSelected = null;
      }

      if (this.allSelectedFromSameParent &&
        (this.selectedConditions[0].parent.getConditionsCount() === this.selectedConditions.length)) {
        this.allChildrenOfSameParentSelected = true;
      } else {
        this.allChildrenOfSameParentSelected = false;
      }
    } else {
      this.allSelectedFromSameParent = false;
      this.allChildrenOfSameParentSelected = false;
      this.parentOfAllSelected = null;
    }
  }
}
